import { Class } from "@common/types/class";

export interface ClassWithChildren extends Class {
    children: ClassWithChildren[];
    depth: number;
}

/**
 * Recursively builds the class tree.
 *
 * Root nodes have `depth === 0` (matching the convention used by the generic
 * `Tree` provider, where `node.depth === 0` identifies roots). Children at every
 * level are sorted by `order`. A `visited` set guards against cycles in the
 * parent/child relationships.
 *
 * The resulting node objects are shared between `classesWithChildren` and
 * `classesMap` so every consumer sees the same recursively-populated tree.
 */
function buildClassTree({
    parentId,
    depth,
    childrenByParentId,
    classesMap,
    visited,
}: {
    parentId: string | null;
    depth: number;
    childrenByParentId: Map<string | null, Class[]>;
    classesMap: Map<string, ClassWithChildren>;
    visited: Set<string>;
}): ClassWithChildren[] {
    const directChildren = childrenByParentId.get(parentId) ?? [];

    return directChildren
        .toSorted((a, b) => a.order - b.order)
        .flatMap((classItem) => {
            if (visited.has(classItem.id)) {
                return [];
            }
            visited.add(classItem.id);

            const node: ClassWithChildren = {
                ...classItem,
                depth,
                children: buildClassTree({
                    parentId: classItem.id,
                    depth: depth + 1,
                    childrenByParentId,
                    classesMap,
                    visited,
                }),
            };
            classesMap.set(node.id, node);
            return [node];
        });
}

function flattenPreOrder(nodes: ClassWithChildren[]): Class[] {
    return nodes.flatMap((node) => [node, ...flattenPreOrder(node.children)]);
}

export function getNormalizedClasses(classes: Class[]) {
    const childrenByParentId = new Map<string | null, Class[]>();
    for (const c of classes) {
        const key = c.parentClassId ?? null;
        childrenByParentId.set(key, [
            ...(childrenByParentId.get(key) ?? []),
            c,
        ]);
    }

    const classesMap = new Map<string, ClassWithChildren>();
    const classesWithChildren = buildClassTree({
        parentId: null,
        depth: 0,
        childrenByParentId,
        classesMap,
        visited: new Set<string>(),
    });

    // Preserve the old "every input class is present in classesMap" guarantee:
    // the recursive builder only reaches classes connected to a null-parent
    // root, so orphans (parentClassId pointing to a missing class, or a
    // parentless cycle) would otherwise be absent. This cannot happen with
    // well-formed full-workspace data, but we add any unreached class as a
    // standalone node to keep this a true no-behavior-change refactor.
    for (const c of classes) {
        if (!classesMap.has(c.id)) {
            classesMap.set(c.id, { ...c, depth: 0, children: [] });
        }
    }

    const sortedClasses = flattenPreOrder(classesWithChildren);

    const sortedClassesIndexMap = new Map<string, number>();
    sortedClasses.forEach((c, index) => {
        sortedClassesIndexMap.set(c.id, index);
    });

    return {
        sortedClasses,
        sortedClassesIndexMap,
        classesWithChildren,
        classesMap,
    };
}

interface GetClassPathParams {
    classesMap: Map<string, ClassWithChildren>;
    id: string;
    separator?: string;
}

/**
 * Builds the full ancestor-to-node label path for a class, e.g.
 * "Region > NA > USA". Walks the `parentClassId` chain via `classesMap` and is
 * cycle-guarded by a visited set. Returns the labels joined by `separator`
 * (defaults to " > "); an unknown id yields an empty string.
 */
export function getClassPath({
    classesMap,
    id,
    separator = " > ",
}: GetClassPathParams): string {
    const labels: string[] = [];
    const visited = new Set<string>();
    let current = classesMap.get(id);
    while (current && !visited.has(current.id)) {
        visited.add(current.id);
        labels.unshift(current.label);
        current = current.parentClassId
            ? classesMap.get(current.parentClassId)
            : undefined;
    }
    return labels.join(separator);
}

interface GetDescendantIdsParams {
    classesMap: Map<string, ClassWithChildren>;
    id: string;
}

export function getDescendantIds({
    classesMap,
    id,
}: GetDescendantIdsParams): Set<string> {
    const descendants = new Set<string>();
    const visited = new Set<string>();
    const walk = (currentId: string) => {
        if (visited.has(currentId)) {
            return;
        }
        visited.add(currentId);
        const node = classesMap.get(currentId);
        if (!node) {
            return;
        }
        for (const child of node.children) {
            descendants.add(child.id);
            walk(child.id);
        }
    };
    walk(id);
    return descendants;
}

/**
 * Tokens are a node's own label plus its descendants' labels (not its ancestors),
 * so a query surfaces a node when it matches the node or any descendant: a matched
 * child keeps its ancestors visible, but a parent match never pulls in descendants.
 * Inverse of the category search labels, which fold parents into children.
 */
export function buildClassSearchTokens(
    nodes: ClassWithChildren[],
): Map<string, string[]> {
    const tokensById = new Map<string, string[]>();

    const collect = (
        node: ClassWithChildren,
        visited: Set<string>,
    ): string[] => {
        if (visited.has(node.id)) {
            return [];
        }
        visited.add(node.id);
        const descendantLabels = node.children.flatMap((child) =>
            collect(child, visited),
        );
        const tokens = [node.label.toLowerCase(), ...descendantLabels];
        tokensById.set(node.id, tokens);
        return tokens;
    };

    for (const node of nodes) {
        collect(node, new Set<string>());
    }

    return tokensById;
}

interface DeselectSiblingsInSameRootParams {
    selectedIds: Set<string>;
    addedClassId: string;
    classesMap: Map<string, ClassWithChildren>;
}

export function deselectSiblingsInSameRoot({
    selectedIds,
    addedClassId,
    classesMap,
}: DeselectSiblingsInSameRootParams): Set<string> {
    const addedClass = classesMap.get(addedClassId);
    if (!addedClass) {
        return selectedIds;
    }

    const result = new Set(selectedIds);
    for (const id of selectedIds) {
        const existing = classesMap.get(id);
        if (
            existing &&
            existing.rootClassId === addedClass.rootClassId &&
            existing.id !== addedClassId
        ) {
            result.delete(id);
        }
    }
    return result;
}

interface CheckClassLabelConflictParams {
    classes: Class[];
    parentClassId: string | null;
    label: string;
    targetClassId?: string;
}

/**
 * Detects a label conflict using SIBLING scope, matching the backend unique
 * constraint `(workspaceId, parentClassId, label)`. A conflict is another class
 * with the same `parentClassId` and the same (case-insensitive) label,
 * excluding `targetClassId`.
 *
 * This mirrors the backend constraint, which applies to every workspace. It is
 * slightly looser than the previous FE-only rule, which also blocked naming a
 * subclass after its parent root (the backend already permits that).
 */
export function checkClassLabelConflict({
    classes,
    parentClassId,
    label,
    targetClassId,
}: CheckClassLabelConflictParams) {
    const conflictingClass = classes.find(
        (c) =>
            c.parentClassId === parentClassId &&
            c.label.toLowerCase() === label.toLowerCase() &&
            c.id !== targetClassId,
    );
    return {
        conflict: !!conflictingClass,
        conflictingClass: conflictingClass ?? null,
    };
}
